import { useEffect, useState } from 'react';

import { useSearchParams } from '@remix-run/react';

import { BOOST_AFFILIATE_COOKIE_NAME } from '../useBoostAffiliate';

export const ACTIVE_UPGRADE_COOKIE_NAME = `active_upgrade_${import.meta.env.VITE_UPGRADE_COOKIE_VERSION ?? 2}`;

const ALLOWED_COOKIE_NAMES = [
  'popup_closed',
  'email_capture_disabled',
  ACTIVE_UPGRADE_COOKIE_NAME,
  'language',
  'visit_token',
  'referral_id',
  'form_preview_banner_disabled',
  'last_resource_guid',
  'admin_logged_in',
  'notifications_read',
  'beehiiv_honey',
  BOOST_AFFILIATE_COOKIE_NAME,
] as const; // Add more cookie names here

export type CookieName = (typeof ALLOWED_COOKIE_NAMES)[number] | string;

export interface SetCookieOptions {
  name: CookieName;
  value: string;
  days: number;
}

export type CookieValues = Partial<Record<CookieName, string>>;

interface UseSetCookie {
  setCookie: (options: SetCookieOptions) => void;
  cookies: CookieValues;
  cookiesSet: boolean;
  removeCookie: (name: CookieName) => void;
}

interface Props {
  initialCookies: Partial<{ [key in CookieName]: boolean }>;
}

const presetCookieValues: Partial<Record<CookieName, SetCookieOptions>> = {
  popup_closed: {
    name: 'popup_closed',
    value: 'true',
    days: 4,
  },
  email_capture_disabled: {
    name: 'email_capture_disabled',
    value: 'true',
    days: 30,
  },
  [ACTIVE_UPGRADE_COOKIE_NAME]: {
    name: ACTIVE_UPGRADE_COOKIE_NAME,
    value: 'true',
    days: 30,
  },
};

const useCookies = ({ initialCookies }: Props): UseSetCookie => {
  const [searchParams] = useSearchParams();
  const [cookies, setCookies] = useState<CookieValues>({});
  const [cookiesSet, setCookiesSet] = useState(false);

  // This is a local state to avoid having to wait for the next render to access this cookie
  const [localStateCookies, setLocalStateCookies] = useState<CookieValues>({});

  const getCookies = (): CookieValues => {
    const cookies = document.cookie.split('; ');
    const cookieValues: CookieValues = {};

    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=');
      const hasTruthyObject = cookieName || cookieValue;
      const inAllowedCookies = ALLOWED_COOKIE_NAMES.includes(cookieName as any);

      if (hasTruthyObject && inAllowedCookies) {
        cookieValues[cookieName as CookieName] = decodeURIComponent(cookieValue);
      }
    }

    return cookieValues;
  };

  const setCookie = ({ name, value, days }: SetCookieOptions) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;

    localStateCookies[name] = value;
    setLocalStateCookies({ ...localStateCookies });
  };

  const removeCookie = (name: CookieName) => {
    // Just sets some arbitrary date in the past to expire cookie in browser
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    const cookiesCopy = { ...localStateCookies };
    delete cookiesCopy?.[name];

    setLocalStateCookies({ ...cookiesCopy });
    // The mount-time snapshot is merged underneath the local state, so it has to drop the cookie
    // too — otherwise callers keep reading the removed value until the next full page load.
    setCookies((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  useEffect(() => {
    setCookies(getCookies());

    // Set initial cookies
    for (const [cookieName, cookieValue] of Object.entries(initialCookies)) {
      const cookieToSet = presetCookieValues[cookieName as CookieName];

      if (cookieValue && cookieToSet) {
        setCookie(cookieToSet);
      }
    }

    // Set referral_id cookie if in query params
    const referralId = searchParams.get('ref');
    if (referralId) {
      setCookie({
        name: 'referral_id',
        value: referralId,
        days: 30,
      });
    }

    setCookiesSet(true);
  }, []);

  return {
    setCookie,
    cookies: { ...cookies, ...localStateCookies },
    cookiesSet,
    removeCookie,
  };
};

export default useCookies;
